// Mirrors the backend enumerations in app/models/enumerations/document_crafting_*.py
// and the API shapes of /api/v1/document-crafting (timestamps are ISO strings).

export enum DocumentCraftingType {
  PRESENTATION = 1,
  ONEPAGER_PARTNER = 2,
  ONEPAGER_SERVICE = 3,
  FLYER = 4,
  CASE_STUDY = 5,
  INVITATION = 6,
  CHECKLIST_GUIDE = 7,
  SOCIAL_POST = 8,
  DIAGRAM = 9,
}

export enum DocumentCraftingVisibility {
  PRIVATE = 0,
  WORKSPACE = 1,
}

export enum DocumentCraftingPermission {
  VIEW = 0,
  EDIT = 1,
}

export enum DocumentCraftingRevisionStatus {
  DRAFT = 0,
  SAVED = 1,
  DISCARDED = 2,
  EXPIRED = 3,
}

export enum DocumentCraftingRevisionOrigin {
  AI = 0,
  MANUAL_UPLOAD = 1,
  DUPLICATE = 2,
}

export enum DocumentCraftingRunKind {
  CREATE = 0,
  REVISE = 1,
  REFINE = 2,
  UPLOAD = 3,
  TRANSLATE = 4,
}

export enum DocumentCraftingRunStatus {
  QUEUED = 0,
  ANALYSING = 1,
  AWAITING_ANSWERS = 2,
  GENERATING = 3,
  RENDERING = 4,
  CHECKING = 5,
  FIXING = 6,
  READY = 7,
  FAILED = 8,
  CANCELLED = 9,
}

export enum DocumentCraftingUploadKind {
  SOURCE = 0,
  EXISTING_MATERIAL = 1,
  PARTNER_LOGO = 2,
  PHOTO = 3,
  LOGO = 4,
  PORTRAIT = 5,
  MANUAL_REVISION = 6,
}

export enum DocumentCraftingLabelScope {
  PROJECT = 0,
  PERSONAL = 1,
}

export enum DocumentCraftingIngestConfidence {
  LOW = 0,
  MEDIUM = 1,
  HIGH = 2,
}

export enum DocumentCraftingTranslationStatus {
  PENDING = 0,
  READY = 1,
  FAILED = 2,
}

/** The backend's `DocumentCraftingRunStatus.terminal()`. */
export const TERMINAL_RUN_STATUSES = [
  DocumentCraftingRunStatus.READY,
  DocumentCraftingRunStatus.FAILED,
  DocumentCraftingRunStatus.CANCELLED,
];

/** The backend's `DocumentCraftingRunStatus.cancellable()`. */
export const CANCELLABLE_RUN_STATUSES = [
  DocumentCraftingRunStatus.QUEUED,
  DocumentCraftingRunStatus.ANALYSING,
  DocumentCraftingRunStatus.AWAITING_ANSWERS,
  DocumentCraftingRunStatus.GENERATING,
];

export const isRunTerminal = (status: DocumentCraftingRunStatus | undefined) =>
  status !== undefined && TERMINAL_RUN_STATUSES.includes(status);

export const MAX_FIX_ROUNDS = 2;

// Plain strings of the API

export const DOCUMENT_LANGUAGES = ["de", "en"] as const;
export type DocumentCraftingLanguage = (typeof DOCUMENT_LANGUAGES)[number];

export const LABEL_COLORS = [
  "navy",
  "blue",
  "cyan",
  "teal",
  "green",
  "amber",
  "rose",
  "grey",
] as const;
export type DocumentCraftingLabelColor = (typeof LABEL_COLORS)[number];

/** Lower snake names of `DocumentCraftingType`. */
export const DOCUMENT_TYPE_KEYS = [
  "presentation",
  "onepager_partner",
  "onepager_service",
  "flyer",
  "case_study",
  "invitation",
  "checklist_guide",
  "social_post",
  "diagram",
] as const;
export type DocumentCraftingTypeKey = (typeof DOCUMENT_TYPE_KEYS)[number];

export type DocumentCraftingEngine = "a4" | "deck" | "social" | "diagram";

/** Run `step` keys (frontend i18n `run.steps.<key>`). */
export type DocumentCraftingRunStep =
  | "reading_sources"
  | "condensing_sources"
  | "analysing"
  | "awaiting_answers"
  | "writing_structure"
  | "writing_content"
  | "laying_out"
  | "checking"
  | "fixing"
  | "rendering_preview"
  | "saving"
  | "converting"
  | "ingesting"
  | "translating";

export type DocumentCraftingRunErrorCode =
  | "validation_failed"
  | "conversion_failed"
  | "llm_failed"
  | "model_not_accessible"
  | "access_revoked"
  | "upload_invalid"
  | "cancelled"
  | "internal";

// Shapes (CONTRACTS §8.2)

export interface Paginated {
  total: number;
  page: number;
  page_size: number;
}

/** null in the API means the user was deleted ("Deleted user"). */
export interface DocumentCraftingUser {
  id: number;
  name: string;
  surname: string;
  email: string;
}

export interface DocumentCraftingLabel {
  id: number;
  scope: DocumentCraftingLabelScope;
  name: string;
  color: DocumentCraftingLabelColor;
  archived: boolean;
  /** Project labels, for workspace managers only. */
  usage_count?: number;
}

export interface DocumentCraftingImageMeta {
  width_px: number;
  height_px: number;
  ratio: number;
  mode: string;
  has_alpha: boolean;
  mean_luminance: number;
  /** Alt text the user gave the picture (binding for the AI). */
  alt?: string;
}

export interface DocumentCraftingUpload {
  id: number;
  kind: DocumentCraftingUploadKind;
  field_key: string | null;
  filename: string;
  content_type: string;
  size: number;
  image_meta: DocumentCraftingImageMeta | null;
  introduced_in_revision_id: number | null;
  created_at: string;
}

export interface DocumentCraftingRunBrief {
  id: number;
  kind: DocumentCraftingRunKind;
  status: DocumentCraftingRunStatus;
  step: DocumentCraftingRunStep | null;
  progress: number;
}

export type DocumentCraftingFindingSeverity = "blocking" | "input";

/** A checker finding (`Finding.to_dict()`); pages and slides are 1-based. */
export interface DocumentCraftingFinding {
  check: string;
  severity: DocumentCraftingFindingSeverity;
  message_key: string;
  message_de: string;
  message_en: string;
  params: Record<string, unknown>;
  page: number | null;
  slide: number | null;
  artboard: string | number | null;
  element: string | null;
  form_field: string | null;
}

export interface DocumentCraftingRefinement {
  change_request: string;
  created_at: string;
}

export interface DocumentCraftingRevisionTranslation {
  language: DocumentCraftingLanguage;
  status: DocumentCraftingTranslationStatus;
}

export interface DocumentCraftingRevisionSummary {
  id: number;
  /** null while the revision is a draft. */
  number: number | null;
  status: DocumentCraftingRevisionStatus;
  origin: DocumentCraftingRevisionOrigin;
  base_revision_id: number | null;
  base_revision_number: number | null;
  created_by: DocumentCraftingUser | null;
  created_at: string;
  saved_by: DocumentCraftingUser | null;
  saved_at: string | null;
  model_id: number | null;
  model_name: string | null;
  change_request: string | null;
  refinements: DocumentCraftingRefinement[];
  findings_count: number;
  input_findings_count: number;
  page_count: number | null;
  diff_summary: string | null;
  ingest_confidence: DocumentCraftingIngestConfidence | null;
  translations: DocumentCraftingRevisionTranslation[];
  has_pdf: boolean;
}

/** Form values keyed by field key; file fields hold lists of upload ids. */
export type DocumentCraftingFormValues = Record<string, unknown>;

/** `revision.inputs` (CONTRACTS §7.5). */
export interface DocumentCraftingRevisionInputs {
  type: DocumentCraftingType;
  language: DocumentCraftingLanguage;
  model_id: number | null;
  working_title: string | null;
  values: DocumentCraftingFormValues;
  upload_ids: number[];
  duplicate_note?: string;
}

export type DocumentCraftingAnswerType =
  | "text"
  | "single_choice"
  | "multi_choice"
  | "date"
  | "number"
  | "url";

export type DocumentCraftingAnswer =
  | { value: unknown }
  | { ai_decides: true };

export interface DocumentCraftingQuestion {
  id: string;
  text: string;
  why: string;
  answer_type: DocumentCraftingAnswerType;
  options: string[];
  is_fact: boolean;
  answer: DocumentCraftingAnswer | null;
  /** A required detail the description did not answer: the answer becomes
   * the value of this form field. */
  field_key?: string | null;
}

export interface DocumentCraftingOutlineItem {
  page?: number;
  slide?: number;
  block: string;
  purpose: string;
  keywords: string[];
  sources: number[];
}

export interface DocumentCraftingOutline {
  items: DocumentCraftingOutlineItem[];
  missing_required_facts: string[];
}

export interface DocumentCraftingRevisionArtefacts {
  native: boolean;
  native_ext: string | null;
  pdf: boolean;
  /** Number of page (slide) PNGs. */
  pages: number;
  thumbnail: boolean;
  /** Diagram PNG at 300 ppi. */
  png?: boolean;
}

export interface DocumentCraftingRevisionDetail
  extends DocumentCraftingRevisionSummary {
  inputs: DocumentCraftingRevisionInputs;
  questions: DocumentCraftingQuestion[] | null;
  outline: DocumentCraftingOutline | null;
  outline_remarks: string | null;
  findings: DocumentCraftingFinding[];
  artefacts: DocumentCraftingRevisionArtefacts;
  ingest_notes: string[];
  uploads: DocumentCraftingUpload[];
  content_schema_version: string | null;
  asset_version: string | null;
  last_run: DocumentCraftingRunBrief | null;
}

export interface DocumentCraftingLatestRevision {
  id: number;
  number: number;
  saved_at: string;
  saved_by: DocumentCraftingUser | null;
  page_count: number | null;
  has_thumbnail: boolean;
}

export interface DocumentCraftingDocumentPermissions {
  can_view: boolean;
  can_edit: boolean;
  can_manage: boolean;
}

export interface DocumentCraftingDocumentListItem {
  id: number;
  title: string;
  type: DocumentCraftingType;
  type_key: DocumentCraftingTypeKey;
  language: DocumentCraftingLanguage;
  visibility: DocumentCraftingVisibility;
  workspace_permission: DocumentCraftingPermission;
  owner: DocumentCraftingUser | null;
  owner_in_workspace: boolean;
  /** A personal document (no workspace): only its owner sees it; no shares,
   * visibility, project labels or owner transfer. */
  is_personal: boolean;
  created_by: DocumentCraftingUser | null;
  created_at: string;
  updated_at: string;
  latest_revision: DocumentCraftingLatestRevision | null;
  revision_count: number;
  project_labels: DocumentCraftingLabel[];
  personal_labels: DocumentCraftingLabel[];
  my_draft: { revision_id: number; last_activity_at: string } | null;
  /** The caller's active run on this document. */
  active_run: DocumentCraftingRunBrief | null;
  /** No saved revision and the create run failed or was cancelled. */
  creation_failed: boolean;
  /** Explicit user shares (current workspace members); 0 for personal documents. */
  share_count: number;
  permissions: DocumentCraftingDocumentPermissions;
}

export interface DocumentCraftingRevisionRef {
  document_id: number;
  revision_id: number;
  revision_number: number;
}

export interface DocumentCraftingDocumentDetail
  extends DocumentCraftingDocumentListItem {
  /** Saved revisions, newest first. */
  revisions: DocumentCraftingRevisionSummary[];
  my_draft_revision: DocumentCraftingRevisionSummary | null;
  usage_visible: boolean;
  duplicated_from: DocumentCraftingRevisionRef | null;
  /** Per-file upload limit that applies to this document: its workspace's
   * setting, or the module default for a personal document. */
  max_upload_mb: number;
}

export interface DocumentCraftingDocumentListResponse extends Paginated {
  documents: DocumentCraftingDocumentListItem[];
}

export type DocumentCraftingListScope = "all" | "mine" | "shared";

export type DocumentCraftingListSort =
  | "last_revision"
  | "created"
  | "title"
  | "type"
  | "revisions";

/** Query of `GET /documents/`; arrays are sent as `type[]=1&type[]=2`. */
export interface DocumentCraftingDocumentListParams {
  scope?: DocumentCraftingListScope;
  q?: string;
  type?: DocumentCraftingType[];
  label?: number[];
  personal_label?: number[];
  language?: DocumentCraftingLanguage;
  created_by?: number;
  /** YYYY-MM-DD, latest saved_at. */
  from?: string;
  to?: string;
  has_draft?: boolean;
  sort?: DocumentCraftingListSort;
  order?: "asc" | "desc";
  page?: number;
  page_size?: number;
}

export interface DocumentCraftingRun {
  id: number;
  document_id: number;
  revision_id: number | null;
  kind: DocumentCraftingRunKind;
  status: DocumentCraftingRunStatus;
  step: DocumentCraftingRunStep | null;
  progress: number;
  fix_round: number;
  max_fix_rounds: number;
  /** The step keys of this run kind, in order. */
  steps: DocumentCraftingRunStep[];
  error: string | null;
  error_code: DocumentCraftingRunErrorCode | null;
  cancel_requested: boolean;
  can_cancel: boolean;
  started_at: string;
  finished_at: string | null;
  /** Computed by the server (until `finished_at`, else until now). */
  elapsed_seconds: number;
  model_id: number | null;
  /** Present once the status is AWAITING_ANSWERS or later. */
  questions?: DocumentCraftingQuestion[] | null;
  outline?: DocumentCraftingOutline | null;
  outline_remarks?: string | null;
  result: {
    revision_id: number;
    revision_number: number | null;
    revision_status: DocumentCraftingRevisionStatus;
  } | null;
}

// Type registry and form schema (CONTRACTS §6.5)

export interface LocalizedText {
  en: string;
  de: string;
}

export type DocumentCraftingFieldType =
  | "text"
  | "textarea"
  | "number"
  | "date"
  | "time"
  | "url"
  | "email"
  | "select"
  | "multiselect"
  | "boolean"
  | "list"
  | "file"
  | "group";

export type DocumentCraftingFormStep = "basics" | "details" | "files";

export type DocumentCraftingFileKind =
  | "source"
  | "existing_material"
  | "partner_logo"
  | "photo"
  | "logo"
  | "portrait";

export type DocumentCraftingPrefill =
  | "user.full_name"
  | "user.email"
  | "settings.contact_phone"
  | "settings.contact_role"
  | "workspace.website_url"
  | "today"
  | null;

/** `required_if`: `field` is an absolute dotted path from the values root
 * without list indices ("contact.phone"); `contains` means a multiselect
 * holds `value`. */
export interface DocumentCraftingRequiredIf {
  field: string;
  condition: "not_empty" | "equals" | "contains";
  value?: unknown;
}

export type DocumentCraftingFieldFormat = "url_or_email" | "phone" | "chart_table";

export interface DocumentCraftingCountLimit {
  max_length?: number | null;
  max_words?: number | null;
}

/** Number of list entries (as a string) → item field key → limits. */
export type DocumentCraftingCountLimits = Record<
  string,
  Record<string, DocumentCraftingCountLimit>
>;

export interface DocumentCraftingFieldOption {
  value: string;
  label: LocalizedText;
}

export interface DocumentCraftingFormField {
  key: string;
  type: DocumentCraftingFieldType;
  step: DocumentCraftingFormStep;
  required: boolean;
  required_if?: DocumentCraftingRequiredIf | null;
  label: LocalizedText;
  help?: LocalizedText | null;
  placeholder?: LocalizedText | null;
  min_length?: number | null;
  max_length?: number | null;
  min?: number | null;
  max?: number | null;
  options?: DocumentCraftingFieldOption[] | null;
  allow_custom?: boolean;
  default?: unknown;
  prefill?: DocumentCraftingPrefill;
  item_fields?: DocumentCraftingFormField[] | null;
  min_items?: number | null;
  max_items?: number | null;
  fields?: DocumentCraftingFormField[] | null;
  file_kind?: DocumentCraftingFileKind | null;
  accept?: string[] | null;
  max_files?: number | null;
  max_size_mb?: number | null;
  min_width_px?: number | null;
  preview_on_navy?: boolean;
  /** false for the briefing: the revise form shows it read-only. */
  editable_in_revision?: boolean;
  /** UI hint: "AI drafts this if empty". */
  ai_fills_if_empty?: boolean;
  /** A detail the description usually answers: optional in the form; the
   * analysis takes it from the description or asks for it when required. */
  ask_if_missing?: boolean;
  // Registry extensions (registry/form_field.py; null when unused)
  max_words?: number | null;
  /** List sizes that are allowed, e.g. 2, 4 or 6. */
  allowed_counts?: number[] | null;
  format?: DocumentCraftingFieldFormat | null;
  /** A boolean list-item field that at most one entry may set. */
  exclusive?: boolean;
  /** Per-item limits that depend on the number of list entries. */
  limits_by_count?: DocumentCraftingCountLimits | null;
}

export interface DocumentCraftingTypeDefinition {
  id: DocumentCraftingType;
  key: DocumentCraftingTypeKey;
  engine: DocumentCraftingEngine;
  outputs: string[];
  native_ext: string | null;
  phase: number;
  enabled: boolean;
  min_pages: number | null;
  max_pages: number | null;
  supports_manual_upload: boolean;
  has_thumbnail: boolean;
  fields: DocumentCraftingFormField[];
  /** Median run cost of this type in the workspace (USD); null below 5 runs. */
  typical_cost: number | null;
}

export interface DocumentCraftingTypesResponse {
  types: DocumentCraftingTypeDefinition[];
}

// Module-level endpoints

export interface DocumentCraftingCapabilities {
  can_use: boolean;
  has_workspace: boolean;
  workspace_id: number | null;
  can_manage_workspace: boolean;
  is_admin: boolean;
  default_model_id: number | null;
}

export interface DocumentCraftingModel {
  model_id: number;
  model_name: string;
  supports_images: boolean;
}

export interface DocumentCraftingModelsResponse {
  models: DocumentCraftingModel[];
  default_model_id: number | null;
  /** The module default (Claude Sonnet 5.5) is not accessible. */
  default_is_fallback: boolean;
}

export interface DocumentCraftingMembersResponse {
  members: DocumentCraftingUser[];
}

// Documents

export interface DocumentCraftingCreatePayload {
  type: DocumentCraftingType;
  language: DocumentCraftingLanguage;
  model_id: number;
  working_title?: string;
  values: DocumentCraftingFormValues;
  duplicated_from_revision_id?: number;
  duplicate_note?: string;
  /** Uploads of the duplicated revision to copy into the new document. */
  reuse_upload_ids?: number[];
  /** Alt texts of the new pictures per field key, in the order of `files[<field_key>]`. */
  upload_alts?: Record<string, string[]>;
}

/** New files per form field key, sent as `files[<field_key>]`. */
export type DocumentCraftingFiles = Record<string, File[]>;

export interface DocumentCraftingCreateResponse {
  document_id: number;
  revision_id: number;
  run_id: number;
}

export interface DocumentCraftingDocumentUpdate {
  title?: string;
  visibility?: DocumentCraftingVisibility;
  workspace_permission?: DocumentCraftingPermission;
  owner_id?: number;
}

export interface DocumentCraftingShare {
  user: DocumentCraftingUser;
  permission: DocumentCraftingPermission;
  granted_by: DocumentCraftingUser | null;
  created_at: string;
}

export interface DocumentCraftingShares {
  visibility: DocumentCraftingVisibility;
  workspace_permission: DocumentCraftingPermission;
  shares: DocumentCraftingShare[];
}

export interface DocumentCraftingSharesUpdate {
  shares: { user_id: number; permission: DocumentCraftingPermission }[];
}

export interface DocumentCraftingDocumentLabelsUpdate {
  project_label_ids?: number[];
  personal_label_ids?: number[];
}

export interface DocumentCraftingDocumentLabels {
  project_labels: DocumentCraftingLabel[];
  personal_labels: DocumentCraftingLabel[];
}

export interface DocumentCraftingDuplicatePrefill {
  prefill: {
    type: DocumentCraftingType;
    language: DocumentCraftingLanguage;
    model_id: number | null;
    working_title: string | null;
    values: DocumentCraftingFormValues;
    uploads: DocumentCraftingUpload[];
  };
  source: DocumentCraftingRevisionRef;
}

// Labels

export interface DocumentCraftingLabelsResponse {
  project: DocumentCraftingLabel[];
  personal: DocumentCraftingLabel[];
}

export interface DocumentCraftingLabelCreate {
  scope: DocumentCraftingLabelScope;
  name: string;
  color: DocumentCraftingLabelColor;
}

export interface DocumentCraftingLabelUpdate {
  name?: string;
  color?: DocumentCraftingLabelColor;
  archived?: boolean;
}

// Settings

export interface DocumentCraftingMySettings {
  contact_phone: string;
  contact_role: string;
  last_model_id: number | null;
  list_view_prefs: Record<string, unknown>;
}

export interface DocumentCraftingWorkspaceSettings {
  default_model_id: number | null;
  company_phone: string;
  website_url: string;
  english_variant: string;
  translation_marker: boolean;
  draft_ttl_days: number;
  max_upload_mb: number;
  /** Read-only: whether the caller may change these settings. */
  can_manage: boolean;
  /** Read-only: false in the personal scope, where these are the module
   * defaults. */
  has_workspace: boolean;
  /** Read-only: the models the workspace grants, which the default model
   * must be one of (empty in the personal scope). */
  available_models?: DocumentCraftingModel[];
}

export type DocumentCraftingWorkspaceSettingsUpdate = Partial<
  Omit<DocumentCraftingWorkspaceSettings, "can_manage" | "has_workspace">
>;

// Revisions, drafts, translations

export interface DocumentCraftingRevisePayload {
  base_revision_id: number;
  change_request: string;
  values: DocumentCraftingFormValues;
  model_id: number;
  /** Earlier uploads not to use for this run (they stay attached). */
  unticked_upload_ids?: number[];
  discard_existing_draft?: boolean;
  /** Alt texts of the new pictures per field key, in the order of `files[<field_key>]`. */
  upload_alts?: Record<string, string[]>;
  /** Changed alt texts of earlier uploads ("" removes it). */
  upload_alt_updates?: Record<string, string>;
}

export interface DocumentCraftingStartedRevision {
  revision_id: number;
  run_id: number;
}

export interface DocumentCraftingUploadRevisionPayload {
  file: File;
  base_revision_id: number;
  note?: string;
  /** Ends the caller's open draft of the document (else 409 `draft_exists`). */
  discard_existing_draft?: boolean;
}

export interface DocumentCraftingRefinePayload {
  change_request: string;
  model_id?: number;
}

export interface DocumentCraftingTranslation {
  id: number;
  language: DocumentCraftingLanguage;
  status: DocumentCraftingTranslationStatus;
  pages: number;
}

/** 202 with `run_id` while it is produced, 200 without when cached. */
export interface DocumentCraftingTranslationResponse {
  run_id?: number;
  translation: DocumentCraftingTranslation;
}

export interface DocumentCraftingUsageRun {
  id: number;
  kind: DocumentCraftingRunKind;
  status: DocumentCraftingRunStatus;
  model_id: number | null;
  model_name: string | null;
  started_at: string;
  finished_at: string | null;
  cost: number;
}

export interface DocumentCraftingUsage {
  total_cost: number;
  runs: DocumentCraftingUsageRun[];
}

// Runs

export interface DocumentCraftingAnswersPayload {
  answers: Record<string, DocumentCraftingAnswer>;
  outline_remarks?: string;
}

// Errors (`{message, code, ...extra}` bodies, CONTRACTS §8.0)

export interface DocumentCraftingApiError {
  message?: string;
  code?: string;
  [extra: string]: unknown;
}

/** 400 `invalid_form`: messages keyed by field path. */
export interface DocumentCraftingInvalidFormError extends DocumentCraftingApiError {
  code: "invalid_form";
  errors: Record<string, string>;
}

/** 409 `draft_exists` from POST /documents/<id>/revisions/. */
export interface DocumentCraftingDraftExistsError extends DocumentCraftingApiError {
  code: "draft_exists";
  draft_revision_id: number;
}

/** 409 `save_conflict` from POST /drafts/<rev>/save/. */
export interface DocumentCraftingSaveConflictError extends DocumentCraftingApiError {
  code: "save_conflict";
  latest_revision: {
    number: number;
    saved_by: DocumentCraftingUser | null;
    saved_at: string;
  };
  base_revision_number: number | null;
  next_number: number;
}

export type DocumentCraftingFileKindPath = "native" | "pdf" | "thumbnail" | "png";
